import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAttribution, clearAttribution, trackEvent } from '../utils/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('biosounds_token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('biosounds_token');
    console.log('[Auth] Checking auth, token exists:', !!storedToken);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      
      console.log('[Auth] /me response status:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('[Auth] User authenticated:', userData.user?.email);
        setUser(userData.user);
        if (storedToken) {
          setToken(storedToken);
        }
      } else {
        console.log('[Auth] Not authenticated, clearing state');
        setUser(null);
        localStorage.removeItem('biosounds_token');
        setToken(null);
      }
    } catch (error) {
      console.error('[Auth] Check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Try to get the response body
      let data = {};
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const textBody = await response.text();
          if (textBody) {
            data = JSON.parse(textBody);
          }
        } catch (e) {
          console.warn('[Auth] Could not parse response body:', e.message);
        }
      }
      
      console.log('[Auth] Login response status:', response.status, 'detail:', data?.detail);
      
      if (!response.ok) {
        // Use status code to determine error type when detail is not available
        // 404 = User not found, 401 = Wrong password, others = generic error
        let errorCode = data?.detail;
        if (!errorCode) {
          if (response.status === 404) {
            errorCode = 'user_not_found';
          } else if (response.status === 401) {
            errorCode = 'invalid_password';
          } else {
            errorCode = 'login_failed';
          }
        }
        throw new Error(errorCode);
      }
      
      if (data.token) {
        console.log('[Auth] Saving token to localStorage');
        localStorage.setItem('biosounds_token', data.token);
        setToken(data.token);
      }
      if (data.user) {
        console.log('[Auth] Setting user:', data.user.email);
        setUser(data.user);
      }
      return data;
    } catch (error) {
      console.error('[Auth] Login error:', error?.message);
      if (error.name === 'AbortError') {
        throw new Error('timeout');
      }
      throw new Error(error?.message || 'network_error');
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name, attribution: getAttribution() })
      });
      
      const data = await response.json();
      console.log('[Auth] Register response:', response.status, !!data.token);
      
      if (!response.ok) {
        const errorMsg = data?.detail || data?.message || 'Registration failed';
        throw new Error(String(errorMsg));
      }
      
      if (data.user?.user_id) {
        trackEvent('CompleteRegistration', {}, `reg-${data.user.user_id}`);
        clearAttribution();
      }
      if (data.token) {
        console.log('[Auth] Saving token to localStorage');
        localStorage.setItem('biosounds_token', data.token);
        setToken(data.token);
      }
      if (data.user) {
        console.log('[Auth] Setting user:', data.user.email);
        setUser(data.user);
      }
      return data;
    } catch (error) {
      const message = error?.message || 'Registration failed';
      throw new Error(String(message));
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });
    
    if (!response.ok) {
      throw new Error('Session exchange failed');
    }
    
    const data = await response.json();
    // Backend returns 'token', not 'session_token'
    const authToken = data.token || data.session_token;
    if (authToken) {
      setToken(authToken);
      localStorage.setItem('biosounds_token', authToken);
      const attribution = getAttribution();
      if (attribution && !data.user?.attribution) {
        fetch(`${API}/auth/attribution`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ attribution })
        }).then(() => clearAttribution()).catch(() => {});
      }
    }
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('biosounds_token');
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      loginWithGoogle,
      exchangeSession,
      logout,
      refreshUser,
      isAuthenticated: !!user,
      hasSubscription: user?.subscription_status === 'active' || user?.subscription_status === 'cancelled'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
